import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Chip,
  Divider,
  MenuItem,
  Pagination,
  Paper,
  Select,
  type SelectChangeEvent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import type { DateRange } from '@mui/x-date-pickers-pro';
import { DateRangePicker, LocalizationProvider, type PickersShortcutsItem } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import { useGetAttractionPerformance, useGetAttractionPerformanceSummary } from '~/hooks/apis/attractions';

import { Dayjs, getDayJs } from '~/services/TimeService';

import type { AttractionPerformanceParams, AttractionPerformanceRow } from '~/types/attractionTypes';
import { formatAttractionType } from '~/types/attractionTypes';

type SortField =
  | 'popularityRank'
  | 'offerImpressions'
  | 'offerClicks'
  | 'offerCtr'
  | 'name'
  | 'bookings'
  | 'revenue'
  | 'ttvAr'
  | 'margin'
  | 'marginPct'
  | 'offerCount';

const IMG_PLACEHOLDER = (
  <Box
    sx={{
      width: 56,
      height: 42,
      borderRadius: 1,
      bgcolor: 'grey.100',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.625rem' }}>
      No img
    </Typography>
  </Box>
);

function PerformanceImage({ src, alt }: { src?: string | null; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return IMG_PLACEHOLDER;
  const url = src.startsWith('http')
    ? src
    : `${window.configs.IMAGE_HOST}/fl_progressive,q_auto:eco,c_fill,g_auto,w_112,h_84/${src}`;
  return (
    <Box
      component="img"
      src={url}
      alt={alt}
      onError={() => setFailed(true)}
      sx={{
        width: 56,
        height: 42,
        objectFit: src.includes('wikimedia') ? 'contain' : 'cover',
        backgroundColor: src.includes('wikimedia') ? '#f5f5f5' : undefined,
        borderRadius: 1,
      }}
    />
  );
}
type SortDirection = 'asc' | 'desc';

const DATE_SHORTCUTS: Array<PickersShortcutsItem<DateRange<Dayjs>>> = [
  { label: 'Last 7 Days', getValue: () => [getDayJs().subtract(7, 'day'), getDayJs().subtract(1, 'day')] },
  { label: 'Last 14 Days', getValue: () => [getDayJs().subtract(14, 'day'), getDayJs().subtract(1, 'day')] },
  { label: 'Last 28 Days', getValue: () => [getDayJs().subtract(28, 'day'), getDayJs().subtract(1, 'day')] },
];

function formatNumber(n: number): string {
  return n.toLocaleString('en-AU');
}

function formatCurrency(n: number | undefined): string {
  if (n === undefined || n === 0) return '--';
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
}

function formatPercent(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function formatMarginPct(n: number | undefined): string {
  if (n === undefined || n === 0) return '--';
  return `${(n * 100).toFixed(1)}%`;
}

function safeDivide(numerator: number, denominator: number): number {
  if (denominator === 0) {
    return 0;
  }
  return numerator / denominator;
}

interface FunnelStep {
  label: string;
  value: number;
  rate: number | null;
}

function ConversionFunnel({ steps, loading }: { steps: Array<FunnelStep>; loading: boolean }) {
  const theme = useTheme();
  if (loading) {
    return <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 1 }} />;
  }

  const maxValue = Math.max(...steps.map((s) => s.value), 1);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1.5, display: 'block' }}>
        Conversion Funnel
      </Typography>
      <Stack spacing={1}>
        {steps.map((step, i) => (
          <Box key={step.label}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="caption" sx={{ minWidth: 130, fontWeight: 500 }}>
                {step.label}
              </Typography>
              <Box sx={{ flex: 1, position: 'relative', height: 24 }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${Math.max((step.value / maxValue) * 100, 1)}%`,
                    bgcolor: theme.palette.primary.main,
                    borderRadius: 0.5,
                    opacity: 1 - i * 0.15,
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 70, textAlign: 'right' }}>
                {formatNumber(step.value)}
              </Typography>
              <Divider orientation="vertical" flexItem />
              <Typography
                variant="caption"
                color={step.rate !== null && step.rate > 0.1 ? 'success.main' : 'text.secondary'}
                sx={{ minWidth: 50, textAlign: 'right', fontWeight: 600 }}
              >
                {step.rate !== null ? formatPercent(step.rate) : '--'}
              </Typography>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  cultural: { bg: '#7C3AED', text: '#fff' },
  natural: { bg: '#059669', text: '#fff' },
  entertainment: { bg: '#DB2777', text: '#fff' },
  religious: { bg: '#D97706', text: '#fff' },
  historical: { bg: '#1A56DB', text: '#fff' },
};

function AttractionTypeChip({ type }: { type: string }) {
  const colors = TYPE_COLORS[type] ?? { bg: '#6B7280', text: '#fff' };
  return (
    <Chip
      label={formatAttractionType(type)}
      size="small"
      sx={{ bgcolor: colors.bg, color: colors.text, fontWeight: 600, fontSize: '0.75rem' }}
    />
  );
}

export default function AttractionPerformancePage() {
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([
    getDayJs().subtract(7, 'day'),
    getDayJs().subtract(1, 'day'),
  ]);
  const [country, setCountry] = useState('AU');
  const [city, setCity] = useState('');
  const [attractionType, setAttractionType] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchName, setSearchName] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState<SortField>('popularityRank');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchName(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const hasCompleteDateRange = !!dateRange[0] && !!dateRange[1];

  const summaryParams = useMemo<AttractionPerformanceParams>(
    () => ({
      from: dateRange[0]?.format('YYYY-MM-DD'),
      to: dateRange[1]?.format('YYYY-MM-DD'),
      countryCode: country || undefined,
    }),
    [dateRange, country],
  );

  const tableParams = useMemo<AttractionPerformanceParams>(
    () => ({
      ...summaryParams,
      city: city || undefined,
      type: attractionType || undefined,
      name: searchName || undefined,
      sort: sortField,
      sortDirection: sortDir,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    }),
    [summaryParams, city, attractionType, searchName, sortField, sortDir, page, rowsPerPage],
  );

  const summaryQuery = useGetAttractionPerformanceSummary(summaryParams, { enabled: hasCompleteDateRange });
  const tableQuery = useGetAttractionPerformance(tableParams, { enabled: hasCompleteDateRange });

  const summary = summaryQuery.data?.result;
  const total = tableQuery.data?.total ?? 0;
  const loading = summaryQuery.isLoading || tableQuery.isLoading;
  const error = summaryQuery.error || tableQuery.error;

  const handleSort = useCallback(
    (field: SortField) => {
      setSortDir((prev) => (sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
      setSortField(field);
      setPage(0);
    },
    [sortField],
  );

  const handleRowsPerPageChange = useCallback((e: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(e.target.value));
    setPage(0);
  }, []);

  const rows = useMemo<Array<AttractionPerformanceRow>>(() => tableQuery.data?.result ?? [], [tableQuery.data?.result]);

  const totalBookings = useMemo(() => rows.reduce((sum, r) => sum + (r.bookings ?? 0), 0), [rows]);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                format="DD/MM/YYYY"
                slotProps={{
                  shortcuts: { items: DATE_SHORTCUTS },
                  textField: { size: 'small', sx: { width: 150 } },
                }}
              />
            </LocalizationProvider>

            <Select
              size="small"
              displayEmpty
              value={country}
              onChange={(e: SelectChangeEvent) => {
                setCountry(e.target.value);
                setCity('');
                setAttractionType('');
                setPage(0);
              }}
              sx={{ flex: 1, minWidth: 120 }}
            >
              <MenuItem value="">All Countries</MenuItem>
              <MenuItem value="AU">Australia</MenuItem>
              <MenuItem value="NZ">New Zealand</MenuItem>
              <MenuItem value="US">United States</MenuItem>
              <MenuItem value="GB">United Kingdom</MenuItem>
              <MenuItem value="SG">Singapore</MenuItem>
              <MenuItem value="MY">Malaysia</MenuItem>
              <MenuItem value="TH">Thailand</MenuItem>
              <MenuItem value="JP">Japan</MenuItem>
              <MenuItem value="ID">Indonesia</MenuItem>
              <MenuItem value="IN">India</MenuItem>
            </Select>

            <Select
              size="small"
              displayEmpty
              value={city}
              onChange={(e: SelectChangeEvent) => {
                setCity(e.target.value);
                setPage(0);
              }}
              sx={{ flex: 1, minWidth: 120 }}
            >
              <MenuItem value="">All Cities</MenuItem>
              <MenuItem value="Sydney">Sydney</MenuItem>
              <MenuItem value="Melbourne">Melbourne</MenuItem>
              <MenuItem value="Brisbane">Brisbane</MenuItem>
              <MenuItem value="Perth">Perth</MenuItem>
              <MenuItem value="Gold Coast">Gold Coast</MenuItem>
              <MenuItem value="Adelaide">Adelaide</MenuItem>
              <MenuItem value="Auckland">Auckland</MenuItem>
            </Select>

            <Select
              size="small"
              displayEmpty
              value={attractionType}
              onChange={(e: SelectChangeEvent) => {
                setAttractionType(e.target.value);
                setPage(0);
              }}
              sx={{ flex: 1, minWidth: 120 }}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="cultural">Cultural</MenuItem>
              <MenuItem value="natural">Natural</MenuItem>
              <MenuItem value="entertainment">Entertainment</MenuItem>
              <MenuItem value="historical">Historical</MenuItem>
              <MenuItem value="religious">Religious</MenuItem>
            </Select>

            {/* TODO: Add State filter once backend exposes distinct state values per country */}
          </Stack>
          <TextField
            size="small"
            fullWidth
            placeholder="Search name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </Stack>
      </Paper>

      {error && <Alert severity="error">Failed to load performance data. {error.message}</Alert>}

      <ConversionFunnel
        loading={loading}
        steps={[
          {
            label: 'Carousel Seen',
            value: summary?.carouselImpressions ?? 0,
            rate: null,
          },
          {
            label: 'Tile Seen',
            value: summary?.tileImpressions ?? 0,
            rate: safeDivide(summary?.tileImpressions ?? 0, summary?.carouselImpressions ?? 0),
          },
          {
            label: 'Tile Clicked',
            value: summary?.tileClicks ?? 0,
            rate: safeDivide(summary?.tileClicks ?? 0, summary?.tileImpressions ?? 0),
          },
          {
            label: 'Offer Seen',
            value: summary?.offerImpressions ?? 0,
            rate: safeDivide(summary?.offerImpressions ?? 0, summary?.tileClicks ?? 0),
          },
          {
            label: 'Offer Clicked',
            value: summary?.offerClicks ?? 0,
            rate: safeDivide(summary?.offerClicks ?? 0, summary?.offerImpressions ?? 0),
          },
          {
            label: 'Booking',
            value: totalBookings,
            rate: safeDivide(totalBookings, summary?.offerClicks ?? 0),
          },
        ]}
      />

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    py: 1,
                    px: 1,
                    whiteSpace: 'nowrap',
                    color: 'white',
                    backgroundColor: 'primary.dark',
                  },
                }}
              >
                <TableCell>Image</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'name'}
                    direction={sortField === 'name' ? sortDir : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Offers</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'bookings'}
                    direction={sortField === 'bookings' ? sortDir : 'desc'}
                    onClick={() => handleSort('bookings')}
                  >
                    Bookings
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'revenue'}
                    direction={sortField === 'revenue' ? sortDir : 'desc'}
                    onClick={() => handleSort('revenue')}
                  >
                    Revenue (AUD)
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'ttvAr'}
                    direction={sortField === 'ttvAr' ? sortDir : 'desc'}
                    onClick={() => handleSort('ttvAr')}
                  >
                    TTV AR (AUD)
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'margin'}
                    direction={sortField === 'margin' ? sortDir : 'desc'}
                    onClick={() => handleSort('margin')}
                  >
                    Margin (AUD)
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'marginPct'}
                    direction={sortField === 'marginPct' ? sortDir : 'desc'}
                    onClick={() => handleSort('marginPct')}
                  >
                    Margin %
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'offerImpressions'}
                    direction={sortField === 'offerImpressions' ? sortDir : 'desc'}
                    onClick={() => handleSort('offerImpressions')}
                  >
                    Offer Impr.
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'offerClicks'}
                    direction={sortField === 'offerClicks' ? sortDir : 'desc'}
                    onClick={() => handleSort('offerClicks')}
                  >
                    Offer Clicks
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'offerCtr'}
                    direction={sortField === 'offerCtr' ? sortDir : 'desc'}
                    onClick={() => handleSort('offerCtr')}
                  >
                    Offer CTR
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 13 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No performance data available for the selected filters and date range.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                rows.map((row) => (
                  <TableRow key={row.attractionId} hover>
                    <TableCell sx={{ width: 56 }}>
                      <PerformanceImage src={row.imageUrl} alt={row.name} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.name}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {row.attractionId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {[row.countryCode, row.state, row.city].filter(Boolean).join(' / ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <AttractionTypeChip type={row.type} />
                    </TableCell>
                    <TableCell align="right">{row.offerCount}</TableCell>
                    <TableCell align="right">
                      {(row.bookings ?? 0) > 0 ? formatNumber(row.bookings ?? 0) : '--'}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.revenue ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.ttvAr ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.margin ?? 0)}</TableCell>
                    <TableCell align="right">{formatMarginPct(row.marginPct ?? 0)}</TableCell>
                    <TableCell align="right">{formatNumber(row.offerImpressions)}</TableCell>
                    <TableCell align="right">{formatNumber(row.offerClicks)}</TableCell>
                    <TableCell align="right">{formatPercent(row.offerCtr)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Pagination
            count={Math.ceil(total / rowsPerPage)}
            page={page + 1}
            onChange={(_, p) => setPage(p - 1)}
            color="primary"
            size="small"
          />
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="caption" color="text.disabled">
              {total} attractions
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                Per page:
              </Typography>
              <Select
                size="small"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                sx={{ fontSize: '0.75rem', height: 28, minWidth: 60 }}
              >
                {[10, 25, 50, 100].map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
